import { useEffect, useMemo, useRef, useState, useCallback, lazy, Suspense } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileDown, FileCode2, FileText, RefreshCw, Loader2, Check, AlertTriangle, Undo2, Redo2, Sheet, Share2, MessageSquare } from "lucide-react";
import { useUndo } from "@/lib/useUndo";
import { decorate } from "@/lib/glass";
import { withCouplings } from "@/lib/corners";
import { useCustomTypologies } from "@/lib/customTypologies";
import { loadLogo, loadLogoVector, LOGO_LIGHT_SRC } from "@/lib/brand";
import { detailsFor, chunk, loadDetailImage } from "@/lib/details";
import { useCatalogues } from "@/context/CatalogueContext";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { newItem, anchorOf } from "@/lib/geometry";
import { exportPdf, exportDxf } from "@/lib/exporters";
import { subtypeOf, loadDrawing, loadVectors, loadSections } from "@/lib/iwin";
import { AppHeader } from "@/components/AppHeader";
import { ItemList } from "@/components/ItemList";
import { ElevationCanvas } from "@/components/ElevationCanvas";
import { ScannerLoader } from "@/components/ScannerLoader";
import { PropertiesPanel } from "@/components/PropertiesPanel";
import { SHEETS, SheetPreview, ViewSwitch } from "@/components/sheets/SheetPreview";
import { ExportDialog } from "@/components/ExportDialog";
import { SourcePdfDialog } from "@/components/SourcePdfDialog";
import { BomDialog } from "@/components/bom/BomDialog";
import { bomSettings } from "@/lib/bom";
import { bomPages } from "@/lib/bomPages";
import { COVER_PER_PAGE } from "@/components/sheets/CoverSheet";
import { ShareDialog } from "@/components/ShareDialog";
import { ReviewSheet } from "@/components/feedback/ReviewSheet";
import { useFeedback, reviewTotals } from "@/lib/feedback";

const ThreeView = lazy(() => import("@/components/ThreeView"));

const n = (v, d) => (Number(v) > 0 ? Number(v) : d);
const sanitize = (it) => ({
  ...it, columns: it.columns.map((c) => n(c, 1)), rows: it.rows.map((r) => n(r, 1)),
  width: it.columns.reduce((a, b) => a + n(b, 1), 0), height: it.rows.reduce((a, b) => a + n(b, 1), 0),
  frame_depth: n(it.frame_depth, 60), frame_width: n(it.frame_width, 50), mullion_width: n(it.mullion_width, 50), sash_width: n(it.sash_width, 50), quantity: n(it.quantity, 1),
});

const HeadBtn = ({ onClick, children, testid, primary, disabled }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled}
    className={`h-8 px-3 rounded-md text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-40 ${primary ? "bg-cyan-500 text-slate-950 hover:bg-cyan-400" : "border border-slate-700 text-slate-200 hover:border-cyan-500 hover:text-cyan-300"}`}>
    {children}
  </button>
);

export default function Editor() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [tb, setTb] = useState({});
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState({ r: 0, c: 0 });
  const [saveState, setSaveState] = useState("saved");
  const [exporting, setExporting] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [exportMode, setExportMode] = useState(null);
  const [exportOpts, setExportOpts] = useState({ cover: true, official: true, catalogue: true, sections: true, assembly: true, bom: true, three: false });
  const [bomOpen, setBomOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [fb, reloadFb] = useFeedback(`/projects/${id}/feedback`);
  const [snaps, setSnaps] = useState({});
  useCustomTypologies();
  const [view, setView] = useState("elevation");
  const dirty = useRef(false);
  const sheetRefs = useRef([]);
  const applySnap = useCallback((snap) => {
    dirty.current = true;
    setItems(snap.items); setTb(snap.tb);
    setActiveId((a) => (snap.items.some((i) => i.id === a) ? a : snap.items[0]?.id || null));
    setSelected({ r: 0, c: 0 });
  }, []);
  const hist = useUndo(items, tb, applySnap);
  useCatalogues();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${id}`);
      setProject(data);
      if (data.status !== "processing") {
        setItems(data.items); setTb(data.title_block || {});
        hist.reset();
        setActiveId((a) => (data.items.some((i) => i.id === a) ? a : data.items[0]?.id || null));
      }
      return data;
    } catch (e) { toast.error(errMsg(e)); }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (project?.status !== "processing") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [project?.status, load]);

  useEffect(() => {
    if (!dirty.current) return;
    setSaveState("saving");
    const t = setTimeout(async () => {
      try {
        await api.put(`/projects/${id}`, { items: items.map(sanitize), title_block: tb, name: tb.project_name || project?.name });
        setSaveState("saved");
      } catch (e) { setSaveState("error"); toast.error(errMsg(e)); }
    }, 800);
    return () => clearTimeout(t);
  }, [items, tb, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutate = (fn) => { hist.record(); dirty.current = true; setItems(fn); };
  const active = items.find((i) => i.id === activeId);
  const safeItems = useMemo(() => withCouplings(items.map(sanitize).map(decorate)), [items]);
  const safeActive = safeItems.find((i) => i.id === activeId);

  const nextTag = (type) => {
    const p = type === "door" ? "D" : "W";
    let k = 1;
    while (items.some((i) => i.tag === `${p}${k}`)) k++;
    return `${p}${k}`;
  };
  const add = (type) => { const it = newItem(nextTag(type), type); mutate((xs) => [...xs, it]); setActiveId(it.id); setSelected({ r: 0, c: 0 }); };
  const duplicate = (iid) => {
    const src = items.find((i) => i.id === iid);
    const it = { ...JSON.parse(JSON.stringify(src)), id: crypto.randomUUID(), tag: nextTag(src.type) };
    mutate((xs) => [...xs, it]); setActiveId(it.id);
  };
  const remove = (iid) => {
    mutate((xs) => xs.filter((i) => i.id !== iid));
    if (iid === activeId) setActiveId(items.find((i) => i.id !== iid)?.id || null);
  };
  const onSelectCell = (rc) => setSelected(active ? anchorOf(active, rc.r, rc.c) : rc);
  const applyFaceToAll = ({ frame_width, mullion_width, sash_width }) => {
    mutate((xs) => xs.map((i) => ({ ...i, frame_width, mullion_width, sash_width })));
    toast.success(`Face widths ${frame_width}/${mullion_width}/${sash_width} mm applied to all ${items.length} items`);
  };
  const updateActive = (next) => {
    mutate((xs) => xs.map((i) => (i.id === next.id ? next : i)));
    setSelected((s) => (next.cells[s.r]?.[s.c] ? anchorOf(next, s.r, s.c) : { r: 0, c: 0 }));
  };
  const rerun = async () => {
    if (!window.confirm("Re-run AI extraction? Current edits for this project will be replaced.")) return;
    try { await api.post(`/projects/${id}/extract`); dirty.current = false; load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const covers = exportMode === "pdf" && exportOpts.cover ? chunk(safeItems, COVER_PER_PAGE).map((group, index, arr) => ({ kind: "cover", page: { items: group, index, pages: arr.length, all: safeItems, approvals: fb.summary } })) : [];
  const sheetList = covers.concat(safeItems.flatMap((it) => [
    ...(exportMode === "dxf" ? [] : [{ kind: "elevation", item: it }]),
    ...(exportMode === "pdf" && exportOpts.three ? [{ kind: "three", item: it }] : []),
    ...(exportMode === "pdf" && exportOpts.official && subtypeOf(it) ? [{ kind: "official", item: it }] : []),
    ...(exportMode === "pdf" && exportOpts.catalogue ? chunk(detailsFor(it), 4).map((details) => ({ kind: "catalogue", item: it, details })) : []), ...(exportOpts.sections ? [{ kind: "sections", item: it }] : []), ...(exportOpts.assembly ? [{ kind: "assembly", item: it }] : []),
  ])).concat(exportMode === "pdf" && exportOpts.bom && safeItems.length ? bomPages(safeItems, bomSettings(tb)).map((page) => ({ kind: "bom", page })) : []);
  const doPdf = async () => {
    setExporting(true);
    try {
      await Promise.all([...safeItems.flatMap((it) => detailsFor(it)).map(loadDetailImage), ...safeItems.map(subtypeOf).filter(Boolean).flatMap((st) => [loadDrawing(st), loadSections(st)])]);
      await Promise.all([loadLogo(), loadLogo(LOGO_LIGHT_SRC)]).catch(() => {});
      const jobs = [
        ...(exportOpts.three ? safeItems.map((it) => ({ key: it.id, item: it, items: safeItems })) : []),
        ...(exportOpts.cover ? safeItems.map((it) => ({ key: `c_${it.id}`, item: it, items: [it], width: 900, height: 600, dist: 2.5 })) : []),
      ];
      if (jobs.length) {
        const { renderSnapshots } = await import("@/lib/three3d");
        setSnaps(await renderSnapshots(jobs, (n, t) => toast.loading(`Rendering 3D pictures ${n}/${t}…`, { id: "pdf-progress" })));
      }
      toast.loading(`Building PDF · ${sheetList.length} sheets…`, { id: "pdf-progress" });
      await new Promise((r) => setTimeout(r, 400));
      await exportPdf(sheetRefs.current.slice(0, sheetList.length).filter(Boolean), `${(tb.project_name || "elevations").replace(/\s+/g, "_")}.pdf`,
        (n, t) => toast.loading(`Building PDF · sheet ${n}/${t}…`, { id: "pdf-progress" }));
      toast.dismiss("pdf-progress");
      toast.success(`PDF exported · ${sheetList.length} sheets`);
      setExportMode(null);
    } catch (e) { toast.dismiss("pdf-progress"); toast.error(`PDF export failed${e?.message ? `: ${e.message}` : ""}`); console.error(e); } finally { setExporting(false); }
  };
  const doDxf = async () => {
    setExporting(true);
    try {
      const official = exportOpts.official ? safeItems.filter(subtypeOf) : [];
      const vecs = await Promise.all(official.map(async (it) => ({ tag: it.tag, name: subtypeOf(it).name, vec: await loadVectors(subtypeOf(it)) })));
      if (exportOpts.sections) await Promise.all(safeItems.map(subtypeOf).filter(Boolean).map(loadSections));
      const logo = tb.hide_logo ? null : await loadLogoVector().catch(() => null);
      await new Promise((r) => setTimeout(r, 400));
      exportDxf(safeItems, sheetRefs.current.slice(0, sheetList.length).filter(Boolean), `${(tb.project_name || "elevations").replace(/\s+/g, "_")}.dxf`, vecs, logo);
      toast.success(`DXF exported · ${safeItems.length} elevations${sheetList.length ? ` + ${sheetList.length} detail sheets` : ""}${vecs.length ? ` + ${vecs.length} official IWIN-S drawings` : ""}`);
      setExportMode(null);
    } catch (e) { toast.error("DXF export failed"); console.error(e); } finally { setExporting(false); }
  };

  if (!project) return <div className="min-h-screen bg-[#090D16] grid place-items-center"><Loader2 className="animate-spin text-cyan-400" /></div>;
  const processing = project.status === "processing";

  return (
    <div className="h-screen flex flex-col bg-[#090D16] text-slate-100">
      <AppHeader>
        <Link to="/dashboard" data-testid="back-to-dashboard" className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:bg-slate-800"><ArrowLeft size={16} /></Link>
        <span className="font-heading font-semibold truncate" data-testid="project-title">{tb.project_name || project.name}</span>
        <span className="font-mono text-[11px] text-slate-500 hidden md:flex items-center gap-1" data-testid="save-status">
          {saveState === "saving" ? <><Loader2 size={12} className="animate-spin" />Saving</> : saveState === "error" ? <span className="text-rose-400">Save failed</span> : <><Check size={12} className="text-emerald-400" />Saved</>}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center rounded-md border border-slate-700 overflow-hidden" data-testid="undo-redo-group">
            <button data-testid="undo-btn" title="Undo (Ctrl+Z)" onClick={hist.undo} disabled={!hist.canUndo || processing}
              className="h-8 px-2.5 flex items-center gap-1.5 text-xs text-slate-200 hover:bg-slate-800 hover:text-cyan-300 disabled:opacity-35 disabled:pointer-events-none transition-colors">
              <Undo2 size={14} /><span className="hidden lg:inline">Undo</span>{hist.steps > 0 && <span className="font-mono text-[10px] text-slate-500" data-testid="undo-count">{hist.steps}</span>}
            </button>
            <div className="w-px h-5 bg-slate-700" />
            <button data-testid="redo-btn" title="Redo (Ctrl+Shift+Z)" onClick={hist.redo} disabled={!hist.canRedo || processing}
              className="h-8 px-2.5 flex items-center gap-1.5 text-xs text-slate-200 hover:bg-slate-800 hover:text-cyan-300 disabled:opacity-35 disabled:pointer-events-none transition-colors">
              <Redo2 size={14} /><span className="hidden lg:inline">Redo</span>
            </button>
          </div>
          {project.storage_path && <HeadBtn testid="view-source-pdf-btn" onClick={() => setPdfOpen(true)}><FileText size={14} /><span className="hidden md:inline">Source PDF</span></HeadBtn>}
          {project.storage_path && <HeadBtn testid="rerun-ai-btn" onClick={rerun} disabled={processing}><RefreshCw size={14} /><span className="hidden md:inline">Re-run AI</span></HeadBtn>}
          <HeadBtn testid="review-btn" onClick={() => setReviewOpen(true)} disabled={processing}>
            <MessageSquare size={14} /><span className="hidden md:inline">Review</span>
            {reviewTotals(safeItems, fb.summary).open > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-slate-950 text-[10px] font-bold grid place-items-center" data-testid="review-open-count">{reviewTotals(safeItems, fb.summary).open}</span>}
          </HeadBtn>
          <HeadBtn testid="share-3d-btn" onClick={() => setShareOpen(true)} disabled={processing || !items.length}><Share2 size={14} /><span className="hidden md:inline">Share 3D</span></HeadBtn>
          <HeadBtn testid="bom-btn" onClick={() => setBomOpen(true)} disabled={processing || !items.length}><Sheet size={14} />BOM</HeadBtn>
          <HeadBtn testid="export-dxf-btn" onClick={() => setExportMode("dxf")} disabled={processing || !items.length}><FileCode2 size={14} />DXF</HeadBtn>
          <HeadBtn testid="export-pdf-btn" primary onClick={() => setExportMode("pdf")} disabled={processing || !items.length}>
            <FileDown size={14} />PDF
          </HeadBtn>
        </div>
      </AppHeader>

      {processing ? <ScannerLoader filename={project.original_filename} progress={project.progress} /> : (
        <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-auto lg:overflow-hidden">
          <ItemList items={safeItems} activeId={activeId} onSelect={(i) => { setActiveId(i); setSelected({ r: 0, c: 0 }); }} onAdd={add} onDuplicate={duplicate} onDelete={remove} review={fb.summary} />
          {safeActive ? (
            <>
              <div className="flex-1 flex flex-col min-w-0 min-h-[60vh] lg:min-h-0">
                {project.status === "failed" && (
                  <div className="px-4 py-2 bg-rose-500/10 border-b border-rose-500/30 text-sm text-rose-200 flex items-center gap-2" data-testid="extraction-error">
                    <AlertTriangle size={14} />AI extraction failed: {project.error}
                  </div>
                )}
                {view === "elevation"
                  ? <ElevationCanvas item={safeActive} projectId={id} selected={selected} onSelectCell={onSelectCell} leading={<ViewSwitch view={view} setView={setView} />} />
                  : view === "3d"
                  ? <Suspense fallback={<div className="flex-1 grid place-items-center"><Loader2 className="animate-spin text-cyan-400" /></div>}><ThreeView item={safeActive} items={safeItems} leading={<ViewSwitch view={view} setView={setView} />} /></Suspense>
                  : <SheetPreview item={safeActive} tb={tb} view={view} leading={<ViewSwitch view={view} setView={setView} />} />}
              </div>
              <PropertiesPanel item={active} update={updateActive} selected={selected} setSelected={setSelected} applyFaceToAll={applyFaceToAll} projectId={id} hasPdf={!!project.storage_path} items={items} tb={tb} setTb={(v) => { hist.record(); dirty.current = true; setTb(v); }} />
            </>
          ) : (
            <div className="flex-1 grid place-items-center p-10 text-center cad-grid" data-testid="empty-editor">
              <div>
                {project.status === "failed" && <p className="text-rose-300 mb-2" data-testid="extraction-error">AI extraction failed: {project.error}</p>}
                {project.status === "empty" && <p className="text-amber-300 mb-2">No windows or doors were detected in this PDF.</p>}
                <p className="text-slate-400">Add a window or door from the left panel{project.storage_path ? ", or re-run the AI." : "."}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {exportMode && (
        <>
          <ExportDialog format={exportMode} opts={exportOpts} setOpts={setExportOpts} count={sheetList.length} items={safeItems.length} catalogueCount={safeItems.reduce((a, it) => a + detailsFor(it).length, 0)} officialCount={safeItems.filter(subtypeOf).length} exporting={exporting} onExport={exportMode === "pdf" ? doPdf : doDxf} onClose={() => setExportMode(null)} />
          <div style={{ position: "fixed", left: -10000, top: 0, width: 1200 }} aria-hidden>
            {sheetList.map((s, i) => {
              const Comp = SHEETS[s.kind];
              return <Comp key={`${s.item?.id || "prj"}-${s.kind}-${i}`} ref={(el) => (sheetRefs.current[i] = el)} item={s.item} tb={tb} sheetNo={i + 1} total={sheetList.length} details={s.details} page={s.page} snap={s.item && snaps[s.item.id]} snaps={snaps} />;
            })}
          </div>
        </>
      )}
      {bomOpen && <BomDialog items={safeItems} tb={tb} setTb={(v) => { hist.record(); dirty.current = true; setTb(v); }} onClose={() => setBomOpen(false)} />}
      <ReviewSheet open={reviewOpen} onOpenChange={setReviewOpen} projectId={id} items={safeItems} fb={fb} reload={reloadFb} onPick={(i) => { setActiveId(i); setSelected({ r: 0, c: 0 }); }} />
      {shareOpen && <ShareDialog projectId={id} projectName={tb.project_name || project.name} onClose={() => setShareOpen(false)} />}
      {pdfOpen && <SourcePdfDialog projectId={id} onClose={() => setPdfOpen(false)} />}
    </div>
  );
}
